import { absoluteUrl } from '../../util.js'

const PDS_EMAIL_CONTENT_TYPE = 'pdsct_email'

/**
 * Get files and folders in a document library folder.
 *
 * siteUrl: url to site
 * path: path to file
 * id: unique folder id
 * spToken: sharepoint access token
 */
export async function getDocumentLibraryFiles ({ siteUrl, path, id }, spToken) {
  const escapedId = escapeODataStringLiteral(id)
  const escapedPath = escapeODataStringLiteral(path)
  const folderBaseUrl = id
    ? `${siteUrl}/_api/Web/GetFolderById('${escapedId}')`
    : `${siteUrl}/_api/Web/GetFolderByServerRelativeUrl('${escapedPath}')`
  const res = await fetch(folderBaseUrl, getRequestOptions(spToken))
  if (!res.ok) {
    throw new Error(`getDocumentLibraryFiles() failed (${res.status})`)
  }

  const folder = (await res.json())?.d || {}
  const folders = await fetchPagedResults(`${folderBaseUrl}/Folders?$top=5000`, spToken, 'getDocumentLibraryFiles')
  const files = await fetchPagedResults(`${folderBaseUrl}/Files?$top=5000`, spToken, 'getDocumentLibraryFiles')

  return {
    ...folder,
    Folders: {
      results: folders
    },
    Files: {
      results: files
    }
  }
}

/**
 * Reads a file from SharePoint.
 * Returns the file in binary as a Uint8Array.
 *
 * uri: Full uri to file
 * spToken: sharepoint access token
 *
 * We assume that uri has the following format
 * https://pergasab.sharepoint.com/sites/foo/some/path/to/file.docx
 *
 * The request to the api is built up using the origin, the site collection
 * and the server relative url.
 */
export async function readSharePointFile (uri, spToken) {
  const parsed = new URL(uri)
  const {
    // https://pergasab.sharepoint.com
    origin,
    // /sites/foo/some/path/to/file.docx
    pathname
  } = parsed

  // /sites/foo
  const siteCollection = pathname.split('/').slice(0, 3).join('/')
  const serverRelativeUrl = escapeODataStringLiteral(pathname)

  // https://pergasab.sharepoint.com/sites/foo/_api...
  const url = `${origin}${siteCollection}/_api/web/GetFileByServerRelativeUrl('${serverRelativeUrl}')/$value`
  const opts = getRequestOptions(spToken)
  opts.headers.Accept = 'application/octet-stream'
  const res = await fetch(url, opts)
  if (!res.ok) {
    const err = new Error(`readFile() failed (${res.status})`)
    err.status = res.status
    if (res.status === 423) {
      err.code = 'ITEM_LOCKED'
    }
    throw err
  }
  return new Uint8Array(await res.arrayBuffer())
}

/**
 * Reads a file from SharePoint as a converted PDF.
 * Returns the file in binary as a Uint8Array.
 */
export async function readSharePointFileAsPdf (uri, graphToken) {
  const shareToken = encodeGraphShareToken(uri)
  const url = `https://graph.microsoft.com/v1.0/shares/${shareToken}/driveItem/content?format=pdf`
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${graphToken}`,
      Accept: 'application/pdf'
    }
  })

  if (!res.ok) {
    const error = new Error(`readSharePointFileAsPdf() failed (${res.status})`)
    error.status = res.status
    throw error
  }

  const data = new Uint8Array(await res.arrayBuffer())
  if (!isPdfBinary(data)) {
    throw new Error('SharePoint file could not be converted to a valid PDF')
  }

  return data
}

function encodeGraphShareToken (uri) {
  const value = typeof btoa === 'function'
    ? btoa(uri)
    : Buffer.from(uri, 'utf8').toString('base64')

  return `u!${value.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')}`
}

function isPdfBinary (data) {
  return data &&
    data.length > 5 &&
    data[0] === 0x25 &&
    data[1] === 0x50 &&
    data[2] === 0x44 &&
    data[3] === 0x46 &&
    data[4] === 0x2D
}

/**
 * Writes a file to SharePoint.
 * Returns metadata about the file and the file item in the document folder.
 *
 * siteUrl: url to site
 * path: server relative url to folder, e.g. `files`, `files/foo`, `files/foo/bar`
 * for contact, project and department root folders for a category the folders would
 * instead start with the category id e.g. `files_34`, `files_34/foo`, `files_34/foo/bar`
 * fileName: file name including file extension
 * fileData: string | Uint8array
 * spToken: sharepoint access token
 */
export async function writeSharePointFile ({
  siteUrl,
  path,
  fileName,
  fileData,
  overwrite = true
}, spToken) {
  const normalizedFileName = String(fileName || '').normalize('NFC')
  const opts = getRequestOptions(spToken)
  opts.headers['IF-MATCH'] = '*'
  opts.method = 'POST'
  opts.body = fileData

  const escapedPath = escapeODataStringLiteral(path)
  const escapedFileName = escapeODataStringLiteral(normalizedFileName)
  const url = `${siteUrl}/_api/web/GetFolderByServerRelativeUrl('${escapedPath}')/Files/add(url='${escapedFileName}',overwrite=${overwrite ? 'true' : 'false'})?$expand=ListItemAllFields,ListItemAllFields/ParentList`
  const res = await fetch(url, opts)
  if (!res.ok) {
    const errorText = await safeReadText(res)
    const baseMessage = `writeSharePointFile() failed (${res.status})`
    const sharePointMessage = getSharePointErrorMessage(errorText)
    const err = new Error(sharePointMessage || (errorText ? `${baseMessage}: ${errorText}` : baseMessage))
    err.status = res.status
    err.debugMessage = errorText
    if (res.status === 423) {
      err.code = 'ITEM_LOCKED'
      const normalizedPath = String(path || '').replace(/\/+$/, '').replace(/^\/+/, '')
      const normalizedWebFileName = normalizedFileName.replace(/^\/+/, '')
      const webRelativePath = `${normalizedPath}/${normalizedWebFileName}`
      err.lockDiagnostics = await getFileLockDiagnosticsByServerRelativeUrl({
        siteUrl,
        serverRelativePath: webRelativePath
      }, spToken)
    }
    throw err
  }

  const result = (await res.json()).d
  const serverRelativeUrl = result.ServerRelativeUrl
  const listItem = result.ListItemAllFields
  const listId = listItem.ParentList.Id
  const listItemUrl = `${siteUrl}/_api/Web/Lists(guid'${listId}')/Items(${listItem.Id})`
  return {
    absoluteUrl: absoluteUrl(siteUrl, serverRelativeUrl),
    serverRelativeUrl,
    listItemUrl
  }
}

/**
 * Overwrites file content for an existing SharePoint file using its absolute URL.
 */
export async function overwriteSharePointFileByAbsoluteUrl ({
  uri,
  fileData
}, spToken) {
  const parsed = new URL(uri)
  const { origin, pathname } = parsed
  const siteCollection = pathname.split('/').slice(0, 3).join('/')
  const serverRelativeUrl = pathname
  const escapedServerRelativeUrl = escapeODataStringLiteral(serverRelativeUrl)

  const opts = getRequestOptions(spToken)
  opts.headers['IF-MATCH'] = '*'
  opts.headers['X-HTTP-Method'] = 'PUT'
  opts.method = 'POST'
  opts.body = fileData

  const url = `${origin}${siteCollection}/_api/web/GetFileByServerRelativeUrl('${escapedServerRelativeUrl}')/$value`
  const res = await fetch(url, opts)
  if (!res.ok) {
    const errorText = await safeReadText(res)
    const baseMessage = `overwriteSharePointFileByAbsoluteUrl() failed (${res.status})`
    const sharePointMessage = getSharePointErrorMessage(errorText)
    const err = new Error(sharePointMessage || (errorText ? `${baseMessage}: ${errorText}` : baseMessage))
    err.status = res.status
    err.debugMessage = errorText
    throw err
  }
}

/**
 * Overwrites an existing file in a folder when SharePoint reports an equivalent-name collision.
 */
export async function overwriteSharePointFileByEquivalentName ({
  siteUrl,
  path,
  fileName,
  fileData
}, spToken) {
  const escapedPath = escapeODataStringLiteral(path)
  const endpoint = `${siteUrl}/_api/web/GetFolderByServerRelativeUrl('${escapedPath}')/Files?$select=Name,ServerRelativeUrl,ListItemAllFields/Id,ListItemAllFields/ParentList/Id&$expand=ListItemAllFields,ListItemAllFields/ParentList&$top=5000`
  const files = await fetchFolderFilesPaged(endpoint, spToken, 'overwriteSharePointFileByEquivalentName')
  const targetKey = toEquivalentNameKey(fileName)
  const match = files.find((item) => toEquivalentNameKey(item?.Name) === targetKey)

  if (!match?.ServerRelativeUrl) {
    const err = new Error('No equivalent file found to overwrite')
    err.code = 'EQUIVALENT_FILE_NOT_FOUND'
    throw err
  }

  const matchedAbsoluteUrl = absoluteUrl(siteUrl, match.ServerRelativeUrl)
  await overwriteSharePointFileByAbsoluteUrl({
    uri: matchedAbsoluteUrl,
    fileData
  }, spToken)

  const listId = match?.ListItemAllFields?.ParentList?.Id
  const listItemId = match?.ListItemAllFields?.Id
  if (!listId || !listItemId) {
    const err = new Error('Equivalent file overwrite succeeded but list item metadata is missing')
    err.code = 'EQUIVALENT_FILE_MISSING_LIST_ITEM'
    throw err
  }

  const listItemUrl = `${siteUrl}/_api/Web/Lists(guid'${listId}')/Items(${listItemId})`

  return {
    absoluteUrl: matchedAbsoluteUrl,
    serverRelativeUrl: match.ServerRelativeUrl,
    listItemUrl
  }
}

async function fetchFolderFilesPaged (startUrl, spToken, scope) {
  return fetchPagedResults(startUrl, spToken, scope)
}

async function fetchPagedResults (startUrl, spToken, scope) {
  const items = []
  let nextUrl = startUrl

  while (typeof nextUrl === 'string' && nextUrl.length > 0) {
    const res = await fetch(nextUrl, getRequestOptions(spToken))
    if (!res.ok) {
      const errorText = await safeReadText(res)
      const baseMessage = `${scope}() failed (${res.status})`
      const sharePointMessage = getSharePointErrorMessage(errorText)
      const err = new Error(sharePointMessage || (errorText ? `${baseMessage}: ${errorText}` : baseMessage))
      err.status = res.status
      err.debugMessage = errorText
      throw err
    }

    const data = await res.json()
    const pageItems = data?.d?.results
    if (Array.isArray(pageItems) && pageItems.length > 0) {
      items.push(...pageItems)
    }

    nextUrl = data?.d?.__next
  }

  return items
}

function toEquivalentNameKey (value) {
  return String(value || '')
    .normalize('NFC')
    .trim()
    .toLowerCase()
}

/**
 * Writes a file in the same SharePoint folder as an existing source file.
 *
 * uri: Full absolute url to existing source file
 * fileName: new file name
 * fileData: Uint8Array
 * spToken: sharepoint access token
 */
export async function writeSharePointFileNextToOriginal ({
  uri,
  fileName,
  fileData
}, spToken) {
  const parsed = new URL(uri)
  const { origin, pathname } = parsed
  const siteCollection = pathname.split('/').slice(0, 3).join('/')
  const folderPath = pathname.split('/').slice(0, -1).join('/')
  const siteUrl = `${origin}${siteCollection}`

  const nextFileName = await getNextAvailableFileName({
    siteUrl,
    folderPath,
    fileName
  }, spToken)

  return writeSharePointFile({
    siteUrl,
    path: folderPath,
    fileName: nextFileName,
    fileData,
    overwrite: false
  }, spToken)
}

async function getNextAvailableFileName ({ siteUrl, folderPath, fileName }, spToken) {
  const { baseName, extension } = splitFileName(fileName)
  const maxCounter = 200

  for (let counter = 0; counter <= maxCounter; counter++) {
    const candidate = counter === 0
      ? fileName
      : `${baseName} (${counter})${extension}`
    const exists = await fileExistsInFolder({
      siteUrl,
      folderPath,
      fileName: candidate
    }, spToken)
    if (!exists) {
      return candidate
    }
  }

  throw new Error('Could not determine a unique file name for PDF copy')
}

async function fileExistsInFolder ({ siteUrl, folderPath, fileName }, spToken) {
  const normalizedFolder = String(folderPath || '').replace(/\/+$/, '').replace(/^\/+/, '')
  const normalizedFile = String(fileName || '').replace(/^\/+/, '')
  const basePath = `${normalizedFolder}/${normalizedFile}`.replace(/\/+/g, '/')
  const webPathname = (() => {
    try {
      return new URL(siteUrl).pathname.replace(/\/+$/, '')
    } catch {
      return ''
    }
  })()

  const candidatePaths = [
    basePath,
    `/${basePath}`,
    `${webPathname}/${basePath}`.replace(/\/+/g, '/')
  ]
    .map(path => String(path || '').trim())
    .filter(Boolean)

  let lastStatus = 404
  for (const candidatePath of [...new Set(candidatePaths)]) {
    const escapedPath = escapeODataStringLiteral(candidatePath)
    const endpoint = `${siteUrl}/_api/web/GetFileByServerRelativeUrl('${escapedPath}')?$select=Exists`
    const res = await fetch(endpoint, getRequestOptions(spToken))

    if (res.status === 404) {
      lastStatus = 404
      continue
    }

    if (!res.ok) {
      lastStatus = res.status
      continue
    }

    const data = await res.json()
    return !!data?.d?.Exists
  }

  if (lastStatus === 404) {
    return false
  }

  throw new Error(`fileExistsInFolder() failed (${lastStatus})`)
}

export async function sharePointFileExistsInFolder ({ siteUrl, folderPath, fileName }, spToken) {
  return fileExistsInFolder({ siteUrl, folderPath, fileName }, spToken)
}

function splitFileName (fileName) {
  const normalized = String(fileName || '').trim()
  const dotIndex = normalized.lastIndexOf('.')
  if (dotIndex <= 0 || dotIndex >= normalized.length - 1) {
    return {
      baseName: normalized,
      extension: ''
    }
  }

  return {
    baseName: normalized.slice(0, dotIndex),
    extension: normalized.slice(dotIndex)
  }
}

function escapeODataStringLiteral (value) {
  return String(value || '').replace(/'/g, "''")
}

async function safeReadText (res) {
  try {
    return await res.text()
  } catch {
    return ''
  }
}

function getSharePointErrorMessage (errorText) {
  const text = String(errorText || '').trim()
  if (!text) {
    return ''
  }

  const parsed = parseSharePointErrorPayload(text)
  if (parsed) {
    return parsed
  }

  const xmlMatch = text.match(/<m:message[^>]*>([\s\S]*?)<\/m:message>|<message[^>]*>([\s\S]*?)<\/message>/i)
  if (xmlMatch) {
    return decodeHtmlEntities((xmlMatch[1] || xmlMatch[2] || '').trim())
  }

  return ''
}

function parseSharePointErrorPayload (value) {
  try {
    const payload = JSON.parse(value)
    const candidates = [
      payload?.error?.message?.value,
      payload?.error?.message,
      payload?.['odata.error']?.message?.value,
      payload?.message?.value,
      payload?.message,
      payload?.Message
    ]

    for (const candidate of candidates) {
      const text = String(candidate || '').trim()
      if (text.length > 0) {
        return text
      }
    }
  } catch {
    return ''
  }

  return ''
}

function decodeHtmlEntities (value) {
  const namedEntities = {
    amp: '&',
    apos: "'",
    quot: '"',
    lt: '<',
    gt: '>',
    nbsp: '\u00A0',
    ndash: '–',
    mdash: '—',
    hellip: '…',
    laquo: '«',
    raquo: '»',
    copy: '©',
    reg: '®',
    trade: '™'
  }

  return String(value || '').replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]+);/g, (fullMatch, token) => {
    const normalized = String(token || '').trim()

    if (!normalized.length) {
      return fullMatch
    }

    if (normalized[0] === '#') {
      const isHex = normalized[1] === 'x' || normalized[1] === 'X'
      const numericPart = isHex ? normalized.slice(2) : normalized.slice(1)
      const codePoint = Number.parseInt(numericPart, isHex ? 16 : 10)
      if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > 0x10FFFF) {
        return fullMatch
      }

      try {
        return String.fromCodePoint(codePoint)
      } catch {
        return fullMatch
      }
    }

    const named = namedEntities[normalized.toLowerCase()]
    return typeof named === 'string' ? named : fullMatch
  })
}

/**
 * Rename a file or folder in a SharePoint document library.
 */
export async function renameDocumentLibraryItem ({
  siteUrl,
  itemType,
  itemId,
  newName
}, spToken) {
  const escapedItemId = escapeODataStringLiteral(itemId)
  const trimmedName = String(newName || '').trim()
  if (trimmedName.length === 0) {
    throw new Error('renameDocumentLibraryItem() failed (empty name)')
  }

  if (itemType === 'file') {
    await renameDocumentLibraryFile(siteUrl, itemId, trimmedName, spToken)
    return
  }

  const endpoint = `${siteUrl}/_api/web/GetFolderById('${escapedItemId}')/ListItemAllFields`

  const opts = getRequestOptions(spToken)
  opts.headers['X-HTTP-Method'] = 'MERGE'
  opts.headers['If-Match'] = '*'
  opts.method = 'POST'
  opts.body = JSON.stringify({
    __metadata: {
      type: 'SP.ListItem'
    },
    FileLeafRef: trimmedName,
    Title: trimmedName
  })

  const maxAttempts = 3
  const retryDelayMs = [0, 300, 900]

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (retryDelayMs[attempt] > 0) {
      await wait(retryDelayMs[attempt])
    }

    const res = await fetch(endpoint, opts)
    if (res.ok) {
      return
    }

    if (res.status === 423 && attempt < maxAttempts - 1) {
      continue
    }

    const err = new Error(`renameDocumentLibraryItem() failed (${res.status})`)
    err.status = res.status
    err.itemType = itemType
    if (res.status === 400 || res.status === 409) {
      err.code = 'INVALID_OR_CONFLICT_NAME'
    } else if (res.status === 423) {
      err.code = 'ITEM_LOCKED'
      err.lockDiagnostics = await getRenameLockDiagnostics({
        siteUrl,
        itemType,
        itemId
      }, spToken)
    }
    throw err
  }
}

async function renameDocumentLibraryFile (siteUrl, itemId, trimmedName, spToken) {
  const escapedItemId = escapeODataStringLiteral(itemId)
  const fileEndpoint = `${siteUrl}/_api/web/GetFileById(guid'${escapedItemId}')?$select=ServerRelativeUrl`
  const fileRes = await fetch(fileEndpoint, getRequestOptions(spToken))
  if (!fileRes.ok) {
    const err = new Error(`renameDocumentLibraryItem() failed (${fileRes.status})`)
    err.status = fileRes.status
    err.itemType = 'file'
    throw err
  }

  const currentUrl = String((await fileRes.json())?.d?.ServerRelativeUrl || '').trim()
  if (currentUrl.length === 0) {
    throw new Error('renameDocumentLibraryItem() failed (missing file url)')
  }

  const slashIndex = currentUrl.lastIndexOf('/')
  const folderPath = slashIndex >= 0 ? currentUrl.slice(0, slashIndex) : ''
  const targetServerRelativeUrl = `${folderPath}/${trimmedName}`
  const encodedTarget = encodeURIComponent(targetServerRelativeUrl)

  const moveEndpoint = `${siteUrl}/_api/web/GetFileById(guid'${escapedItemId}')/MoveTo(newurl='${encodedTarget}',flags=1)`
  const opts = getRequestOptions(spToken)
  opts.headers['If-Match'] = '*'
  opts.method = 'POST'

  const maxAttempts = 3
  const retryDelayMs = [0, 300, 900]

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (retryDelayMs[attempt] > 0) {
      await wait(retryDelayMs[attempt])
    }

    const moveRes = await fetch(moveEndpoint, opts)
    if (moveRes.ok) {
      return
    }

    if (moveRes.status === 423 && attempt < maxAttempts - 1) {
      continue
    }

    const err = new Error(`renameDocumentLibraryItem() failed (${moveRes.status})`)
    err.status = moveRes.status
    err.itemType = 'file'
    if (moveRes.status === 400 || moveRes.status === 409) {
      err.code = 'INVALID_OR_CONFLICT_NAME'
    } else if (moveRes.status === 423) {
      err.code = 'ITEM_LOCKED'
      err.lockDiagnostics = await getRenameLockDiagnostics({
        siteUrl,
        itemType: 'file',
        itemId
      }, spToken)
    }
    throw err
  }
}

function wait (ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function getRenameLockDiagnostics ({ siteUrl, itemType, itemId }, spToken) {
  const escapedItemId = escapeODataStringLiteral(itemId)
  const endpoint = itemType === 'file'
    ? `${siteUrl}/_api/web/GetFileById(guid'${escapedItemId}')?$select=Name,CheckOutType,TimeLastModified,LockedByUser/Title,LockedByUser/Email,CheckOutByUser/Title,CheckOutByUser/Email&$expand=LockedByUser,CheckOutByUser`
    : `${siteUrl}/_api/web/GetFolderById('${escapedItemId}')?$select=Name,ServerRelativeUrl,ItemCount,TimeLastModified`

  try {
    const res = await fetch(endpoint, getRequestOptions(spToken))
    if (!res.ok) {
      return {
        diagnosticsStatus: res.status,
        diagnosticsError: `getRenameLockDiagnostics() failed (${res.status})`
      }
    }

    const payload = (await res.json())?.d || {}
    if (itemType === 'file') {
      return {
        name: payload.Name,
        checkOutType: payload.CheckOutType,
        timeLastModified: payload.TimeLastModified,
        lockedByUser: payload.LockedByUser || null,
        checkOutByUser: payload.CheckOutByUser || null
      }
    }

    const folderDiagnostics = {
      name: payload.Name,
      serverRelativeUrl: payload.ServerRelativeUrl,
      itemCount: payload.ItemCount,
      timeLastModified: payload.TimeLastModified
    }

    const filesEndpoint = `${siteUrl}/_api/web/GetFolderById('${escapedItemId}')/Files?$select=Name,CheckOutType,TimeLastModified,LockedByUser/Title,LockedByUser/Email,CheckOutByUser/Title,CheckOutByUser/Email&$expand=LockedByUser,CheckOutByUser`
    try {
      const filesPayload = await fetchPagedResults(`${filesEndpoint}&$top=5000`, spToken, 'getRenameLockDiagnostics(files)')
      const lockedFiles = filesPayload
        .filter((file) => {
          const hasLockedByUser = hasMaterializedUser(file?.LockedByUser)
          const hasCheckOutByUser = hasMaterializedUser(file?.CheckOutByUser)
          const hasCheckedOutType = typeof file?.CheckOutType === 'number' && file.CheckOutType !== 2
          return hasLockedByUser || hasCheckOutByUser || hasCheckedOutType
        })
        .map((file) => ({
          name: file.Name,
          checkOutType: file.CheckOutType,
          timeLastModified: file.TimeLastModified,
          lockedByUser: materializedUserOrNull(file.LockedByUser),
          checkOutByUser: materializedUserOrNull(file.CheckOutByUser)
        }))

      folderDiagnostics.lockedFiles = lockedFiles
      return folderDiagnostics
    } catch (err) {
      folderDiagnostics.filesDiagnosticsError = err.message
      return folderDiagnostics
    }
  } catch (err) {
    return {
      diagnosticsError: err.message
    }
  }
}

function hasMaterializedUser (user) {
  return !!materializedUserOrNull(user)
}

function materializedUserOrNull (user) {
  if (!user || typeof user !== 'object') {
    return null
  }

  const title = typeof user.Title === 'string' ? user.Title.trim() : ''
  const email = typeof user.Email === 'string' ? user.Email.trim() : ''
  if (!title && !email) {
    return null
  }

  return {
    Title: title,
    Email: email
  }
}

async function getFileLockDiagnosticsByServerRelativeUrl ({ siteUrl, serverRelativePath }, spToken) {
  try {
    const basePath = String(serverRelativePath || '').trim()
    if (!basePath) {
      return {
        diagnosticsError: 'getFileLockDiagnosticsByServerRelativeUrl() missing serverRelativePath'
      }
    }

    const webPathname = (() => {
      try {
        return new URL(siteUrl).pathname.replace(/\/+$/, '')
      } catch {
        return ''
      }
    })()

    const candidatePaths = [
      basePath,
      `/${basePath.replace(/^\/+/, '')}`,
      `${webPathname}/${basePath.replace(/^\/+/, '')}`.replace(/\/+/g, '/')
    ].map((value) => value.trim()).filter(Boolean)

    let lastError = null
    for (const candidatePath of [...new Set(candidatePaths)]) {
      const escapedPath = escapeODataStringLiteral(candidatePath)
      const endpoint = `${siteUrl}/_api/web/GetFileByServerRelativeUrl('${escapedPath}')?$select=Name,ServerRelativeUrl,CheckOutType,TimeLastModified,LockedByUser/Title,LockedByUser/Email,CheckOutByUser/Title,CheckOutByUser/Email&$expand=LockedByUser,CheckOutByUser`
      const res = await fetch(endpoint, getRequestOptions(spToken))
      if (!res.ok) {
        lastError = {
          diagnosticsStatus: res.status,
          diagnosticsError: `getFileLockDiagnosticsByServerRelativeUrl() failed (${res.status})`,
          diagnosticsPath: candidatePath
        }
        continue
      }

      const payload = (await res.json())?.d || {}
      return {
        name: payload.Name,
        serverRelativeUrl: payload.ServerRelativeUrl,
        checkOutType: payload.CheckOutType,
        timeLastModified: payload.TimeLastModified,
        lockedByUser: materializedUserOrNull(payload.LockedByUser),
        checkOutByUser: materializedUserOrNull(payload.CheckOutByUser)
      }
    }

    return lastError || {
      diagnosticsError: 'getFileLockDiagnosticsByServerRelativeUrl() failed (no candidate path resolved)'
    }
  } catch (err) {
    return {
      diagnosticsError: err.message
    }
  }
}

/**
 * Delete a file or folder in a SharePoint document library.
 */
export async function deleteDocumentLibraryItem ({
  siteUrl,
  itemType,
  itemId,
  permanentDelete = false
}, spToken) {
  const escapedItemId = escapeODataStringLiteral(itemId)
  const baseEndpoint = itemType === 'file'
    ? `${siteUrl}/_api/web/GetFileById(guid'${escapedItemId}')`
    : `${siteUrl}/_api/web/GetFolderById('${escapedItemId}')`
  const endpoint = permanentDelete ? baseEndpoint : `${baseEndpoint}/recycle()`

  const opts = getRequestOptions(spToken)
  opts.method = 'POST'
  if (permanentDelete) {
    opts.headers['X-HTTP-Method'] = 'DELETE'
    opts.headers['If-Match'] = '*'
  }

  const res = await fetch(endpoint, opts)
  if (!res.ok) {
    const err = new Error(`deleteDocumentLibraryItem() failed (${res.status})`)
    err.status = res.status
    err.itemType = itemType
    if (itemType === 'folder') {
      if (res.status === 423) {
        err.code = 'ITEM_LOCKED'
      }

      // Capture lock diagnostics for human-friendly folder delete messages.
      err.lockDiagnostics = await getRenameLockDiagnostics({
        siteUrl,
        itemType,
        itemId
      }, spToken)
    } else if (res.status === 423) {
      err.code = 'ITEM_LOCKED'
    }
    throw err
  }
}

/**
 * Update an email list item after it has been created.
 * siteUrl: site url
 * listItemUrl: url to list item
 * fields: email field properties
 * spToken: sharepoint access token
 */
export async function updateEmailListItemFields ({
  siteUrl,
  listItemUrl,
  fields,
  additionalFields
}, spToken) {
  const contentTypes = await getSiteContentTypes(siteUrl, spToken)
  const email = contentTypes.find(({ Name }) => Name === PDS_EMAIL_CONTENT_TYPE)
  if (!email) {
    throw new Error('No email content-type found')
  }

  const data = {
    __metadata: {
      type: 'SP.ListItem'
    },
    ContentTypeId: email.StringId,
    Title: fields.subject,
    pdscol_document_class: fields.documentClass,
    pdscol_document_date: fields.documentDate,
    pdscol_email_receiver_address: fields.to,
    pdscol_email_send_address: fields.sender,
    pdscol_email_sent_time: fields.dateTimeCreated,
    ...normalizeListItemFields(additionalFields)
  }

  const opts = getRequestOptions(spToken)
  opts.headers['X-HTTP-Method'] = 'MERGE'
  opts.headers['If-Match'] = '*'
  opts.method = 'POST'
  opts.body = JSON.stringify(data)

  const res = await fetch(listItemUrl, opts)
  if (!res.ok) {
    const err = new Error(`updateEmailListItemFields() failed (${res.status})`)
    err.status = res.status
    if (res.status === 423) {
      err.code = 'ITEM_LOCKED'
      err.lockDiagnostics = await getFileLockDiagnosticsFromListItemUrl({
        listItemUrl,
        spToken
      })
    }
    throw err
  }
}

/**
 * Update list item fields for a document library item.
 * Keeps current content type and updates only provided fields.
 */
export async function updateListItemFields ({
  listItemUrl,
  fields
}, spToken) {
  const data = {
    __metadata: {
      type: 'SP.ListItem'
    },
    ...normalizeListItemFields(fields)
  }

  const opts = getRequestOptions(spToken)
  opts.headers['X-HTTP-Method'] = 'MERGE'
  opts.headers['If-Match'] = '*'
  opts.method = 'POST'
  opts.body = JSON.stringify(data)

  const res = await fetch(listItemUrl, opts)
  if (!res.ok) {
    const err = new Error(`updateListItemFields() failed (${res.status})`)
    err.status = res.status
    if (res.status === 423) {
      err.code = 'ITEM_LOCKED'
      err.lockDiagnostics = await getFileLockDiagnosticsFromListItemUrl({
        listItemUrl,
        spToken
      })
    }
    throw err
  }
}

async function getFileLockDiagnosticsFromListItemUrl ({ listItemUrl, spToken }) {
  try {
    const detailsEndpoint = `${listItemUrl}${listItemUrl.includes('?') ? '&' : '?'}$select=FileRef,FileLeafRef`
    const detailsRes = await fetch(detailsEndpoint, getRequestOptions(spToken))
    if (!detailsRes.ok) {
      return {
        diagnosticsStatus: detailsRes.status,
        diagnosticsError: `getFileLockDiagnosticsFromListItemUrl() failed (${detailsRes.status})`
      }
    }

    const payload = (await detailsRes.json())?.d || {}
    const serverRelativePath = String(payload.FileRef || '').trim()
    if (!serverRelativePath) {
      return {
        diagnosticsError: 'getFileLockDiagnosticsFromListItemUrl() missing FileRef'
      }
    }

    const siteUrl = listItemUrl.split('/_api/')[0]
    return getFileLockDiagnosticsByServerRelativeUrl({
      siteUrl,
      serverRelativePath
    }, spToken)
  } catch (err) {
    return {
      diagnosticsError: err.message
    }
  }
}

function normalizeListItemFields (fields) {
  if (!fields || typeof fields !== 'object') {
    return {}
  }

  return Object.keys(fields).reduce((acc, key) => {
    const value = fields[key]
    if (typeof value === 'undefined') {
      return acc
    }
    acc[key] = value
    return acc
  }, {})
}

/**
 * Returns available content types.
 * siteUrl: site url
 * spToken: sharepoint access token
 */
async function getSiteContentTypes (siteUrl, spToken) {
  return fetchPagedResults(`${siteUrl}/_api/Web/AvailableContentTypes?$top=5000`, spToken, 'getSiteContentTypes')
}

/**
 * Search files in a document library.
 *
 * siteUrl: url to site
 * serverRelativeUrl: server relative url to list
 * query: search string
 * onResults: callback on results
 * onEnd: callback when finished
 * spToken: sharepoint access token
 */
export async function searchDocumentLibrary ({
  siteUrl,
  serverRelativeUrl,
  query,
  onResults,
  onEnd
}, spToken) {
  const opts = getRequestOptions(spToken)

  const GET = async (url) => {
    const res = await fetch(url, opts)
    if (!res.ok) {
      throw new Error(`partial search failed (${res.status})`)
    }
    const data = await res.json()
    const { results } = data.d
    if (Array.isArray(results)) {
      onResults(results.map(f => f.File))
    }

    const nextUrl = data?.d?.__next
    if (typeof nextUrl === 'string') {
      await GET(nextUrl)
    } else {
      onEnd()
    }
  }

  const list = await getList(siteUrl, serverRelativeUrl, opts)
  const escapedListId = escapeODataStringLiteral(list.Id)
  const escapedQuery = escapeODataStringLiteral(query)
  const startUrl = `${siteUrl}/_api/Web/Lists/getById('${escapedListId}')/items?$expand=File,FieldValuesAsText&$select=File,FieldValuesAsText&$filter=FSObjType eq '0' and substringof('${escapedQuery}',FileLeafRef)`
  await GET(startUrl)
}

/**
 * Get a list by its relative url.
 *
 * siteUrl: url to site
 * serverRelativeUrl: server relative url to list
 * opts: auth options
 */
async function getList (siteUrl, listRelativeUrl, opts) {
  const select = '$select=Title,RootFolder/ServerRelativeUrl'
  const expand = '$expand=RootFolder'
  const escapedListRelativeUrl = escapeODataStringLiteral(listRelativeUrl)
  const filter = `$filter=RootFolder/ServerRelativeUrl eq '${escapedListRelativeUrl}'`
  const url = `${siteUrl}/_api/web/lists?${select}&${expand}&${filter}`

  const res = await fetch(url, opts)
  if (!res.ok) {
    throw new Error(`selecting list failed (${res.status})`)
  }

  const data = await res.json()
  if (Array.isArray(data.d?.results) && data.d.results.length > 0) {
    const listUri = data.d.results[0].__metadata.id
    const res = await fetch(listUri, opts)
    if (!res.ok) {
      throw new Error(`fetching list data failed (${res.status})`)
    }
    return (await res.json()).d
  } else {
    throw new Error(`no relative url for site '${siteUrl}'`)
  }
}

/**
 * Returns request options needed to interact with the SharePoint REST api.
 */
function getRequestOptions (token) {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json;odata=verbose',
      'Content-Type': 'application/json;odata=verbose'
    }
  }
}
